/**
 * The keyboard rules of a menu button and its menu, as pure functions. The
 * DOM code in `toAppMenu.ts` asks these what to do and then does it, so the
 * rules have tests that need no browser.
 */

/** What a key press asks the menu to do. */
export type MenuAction =
  /** Open the menu if it is closed, then focus the item at `index`. */
  | { readonly kind: 'focus'; readonly index: number }
  /** Close the menu. `returnFocus` says whether focus goes back to the button. */
  | { readonly kind: 'close'; readonly returnFocus: boolean }
  /** The key means nothing to the menu. Leave the event alone. */
  | { readonly kind: 'ignore' }

const IGNORE: MenuAction = { kind: 'ignore' }

/** The index `step` places from `current`, wrapping at both ends. */
export const wrapIndex = (current: number, step: number, count: number): number =>
  ((current + step) % count + count) % count

/**
 * What a key does while the focus is on the menu button.
 * Arrow down opens the menu on the first item. Arrow up opens it on the last.
 * Escape closes it and keeps focus on the button.
 */
export const resolveButtonKey = (key: string, itemCount: number): MenuAction => {
  if (itemCount === 0) {
    return IGNORE
  }
  if (key === 'ArrowDown') {
    return { kind: 'focus', index: 0 }
  }
  if (key === 'ArrowUp') {
    return { kind: 'focus', index: itemCount - 1 }
  }
  if (key === 'Escape') {
    return { kind: 'close', returnFocus: true }
  }
  return IGNORE
}

/**
 * What a key does while the focus is on a menu item. `currentIndex` is the
 * focused item, or -1 when focus is on none of them.
 * Arrows move and wrap. Home and End jump to the ends. Escape closes the menu
 * and returns focus to the button. Tab closes it and lets focus move on.
 */
export const resolveItemKey = (key: string, currentIndex: number, itemCount: number): MenuAction => {
  if (itemCount === 0) {
    return IGNORE
  }
  if (key === 'ArrowDown') {
    return { kind: 'focus', index: wrapIndex(currentIndex, 1, itemCount) }
  }
  if (key === 'ArrowUp') {
    return { kind: 'focus', index: wrapIndex(currentIndex, -1, itemCount) }
  }
  if (key === 'Home') {
    return { kind: 'focus', index: 0 }
  }
  if (key === 'End') {
    return { kind: 'focus', index: itemCount - 1 }
  }
  if (key === 'Escape') {
    return { kind: 'close', returnFocus: true }
  }
  if (key === 'Tab') {
    return { kind: 'close', returnFocus: false }
  }
  return IGNORE
}
